import { EMAIL_STATUS_LABELS, type EmailDetailDTO } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Badge, Button, Field, Input, Select, SidePanel, Skeleton, Textarea, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { api, errorMessage } from '../../lib/api';
import { formatDateTime } from '../../lib/format';

/** One email: headers, the rendered body, every delivery attempt and provider event, and the actions it allows. */
export function EmailDetailDrawer({ emailId, onClose }: { emailId: string; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [showHtml, setShowHtml] = useState(false);
  const [note, setNote] = useState('');
  const { data } = useQuery({
    queryKey: ['emails', emailId],
    queryFn: ({ signal }) => api.get<EmailDetailDTO>(`/api/emails/${emailId}`, { signal }),
  });

  const act = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) => api.post(`/api/emails/${emailId}/${path}`, body ?? {}),
    onSuccess: async () => {
      toast.success('Done');
      await queryClient.invalidateQueries({ queryKey: ['emails'] });
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <SidePanel open onClose={onClose} title={data?.subject ?? 'Email'} width="xl">
      {!data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="space-y-5 text-[13px]">
          <div className="flex flex-wrap items-center gap-2">
            <Badge color={data.status === 'failed' || data.status === 'bounced' ? 'red' : data.status === 'uncertain' ? 'amber' : 'slate'}>
              {EMAIL_STATUS_LABELS[data.status]}
            </Badge>
            <Badge color="violet">{data.source}</Badge>
            {data.businessId && (
              <Link to={`/leads/${data.businessId}`} className="text-ember-700 hover:underline" onClick={onClose}>
                {data.businessName}
              </Link>
            )}
          </div>

          <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1">
            {data.headers.map((header) => (
              <div key={header.name} className="contents">
                <dt className="text-slate-500">{header.name}</dt>
                <dd className="break-all text-slate-800">{header.value}</dd>
              </div>
            ))}
            {data.scheduledFor && (
              <>
                <dt className="text-slate-500">Scheduled</dt>
                <dd>{formatDateTime(data.scheduledFor)}</dd>
              </>
            )}
            {data.sentAt && (
              <>
                <dt className="text-slate-500">Sent</dt>
                <dd>{formatDateTime(data.sentAt)}</dd>
              </>
            )}
          </dl>

          {data.failureReason && <p className="rounded-md border border-red-200 bg-red-50 p-2 text-red-800">{data.failureReason}</p>}
          {data.cancelledReason && <p className="rounded-md border border-slate-200 bg-slate-50 p-2 text-slate-700">Cancelled: {data.cancelledReason}</p>}

          <div>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Message</h3>
              {data.bodyHtml && (
                <Button size="sm" variant="ghost" onClick={() => setShowHtml((current) => !current)}>
                  {showHtml ? 'Plain text' : 'HTML'}
                </Button>
              )}
            </div>
            {showHtml && data.bodyHtml ? (
              <iframe title="Email body" sandbox="" className="h-80 w-full rounded border border-slate-200 bg-white dark:brightness-90" srcDoc={data.bodyHtml} />
            ) : (
              <pre className="whitespace-pre-wrap rounded-md border border-slate-200 bg-surface p-3 font-sans text-slate-700">{data.bodyText}</pre>
            )}
          </div>

          {(data.attempts.length > 0 || data.events.length > 0) && (
            <div>
              <h3 className="mb-1 font-semibold text-slate-900">Delivery log</h3>
              <ul className="space-y-1 text-xs text-slate-600">
                {data.attempts.map((attempt) => (
                  <li key={attempt.id}>
                    Attempt {attempt.attemptNo} via {attempt.provider}: <strong>{attempt.outcome ?? 'in progress'}</strong> · {formatDateTime(attempt.startedAt)}
                    {attempt.error && <span className="text-red-600"> — {attempt.error}</span>}
                  </li>
                ))}
                {data.events.map((event) => (
                  <li key={event.id}>
                    {event.type} ({event.provider}) · {formatDateTime(event.occurredAt)}
                    {event.detail && ` — ${event.detail}`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
            {(data.status === 'scheduled' || data.status === 'pending_approval') && can('email:send') && (
              <Button variant="danger" onClick={() => act.mutate({ path: 'cancel', body: { reason: 'Cancelled from the email log' } })}>
                Cancel send
              </Button>
            )}
            {data.status === 'pending_approval' && can('email:approve') && (
              <>
                <Button variant="primary" onClick={() => act.mutate({ path: 'approve' })}>
                  Approve
                </Button>
                <Button onClick={() => act.mutate({ path: 'reject', body: { reason: 'Rejected in review' } })}>Reject</Button>
              </>
            )}
          </div>

          {data.status === 'uncertain' && can('email:approve') && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="mb-2 text-amber-900">
                The worker stopped while handing this email to the provider, so we can't tell whether it went out. Check the provider's activity log (or ask
                the recipient), then record the outcome. It is <strong>never</strong> resent automatically.
              </p>
              <Textarea rows={2} placeholder="What did you check?" value={note} onChange={(event) => setNote(event.target.value)} />
              <div className="mt-2 flex gap-2">
                <Button onClick={() => act.mutate({ path: 'resolve-uncertain', body: { outcome: 'sent', note: note || undefined } })}>It was delivered</Button>
                <Button onClick={() => act.mutate({ path: 'resolve-uncertain', body: { outcome: 'not_sent', note: note || undefined } })}>It was not sent</Button>
              </div>
            </div>
          )}

          {data.direction === 'inbound' && !data.businessId && <AssignReply emailId={emailId} onDone={onClose} />}
        </div>
      )}
    </SidePanel>
  );
}

/** Link a reply the system could not match (unknown sender) to the right lead. */
function AssignReply({ emailId, onDone }: { emailId: string; onDone: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [businessId, setBusinessId] = useState('');
  const [search, setSearch] = useState('');
  const { data } = useQuery({
    queryKey: ['search', search],
    queryFn: ({ signal }) => api.get<{ results: { type: string; businessId: string; title: string; subtitle: string | null }[] }>(`/api/search?q=${encodeURIComponent(search)}&limit=10`, { signal }),
    enabled: search.trim().length >= 2,
  });
  const leads = (data?.results ?? []).filter((result, index, all) => all.findIndex((r) => r.businessId === result.businessId) === index);
  const assign = useMutation({
    mutationFn: () => api.post(`/api/emails/${emailId}/assign`, { businessId }),
    onSuccess: async () => {
      toast.success('Reply linked — the lead’s timeline and next action were updated');
      await queryClient.invalidateQueries({ queryKey: ['emails'] });
      onDone();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <h3 className="mb-2 font-semibold text-slate-900">Link this reply to a lead</h3>
      <Field label="Find the pizzeria">
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name, email or phone" />
      </Field>
      {leads.length > 0 && (
        <Field label="Lead" className="mt-2">
          <Select value={businessId} onChange={(event) => setBusinessId(event.target.value)} placeholder="Pick a lead">
            {leads.map((lead) => (
              <option key={lead.businessId} value={lead.businessId}>
                {lead.type === 'business' ? lead.title : (lead.subtitle ?? lead.title)}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <Button className="mt-2" variant="primary" disabled={!businessId} loading={assign.isPending} onClick={() => assign.mutate()}>
        Link reply
      </Button>
    </div>
  );
}
